import { AfterViewInit, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { CalculationModelNames, CalculationStatus, CaseWorker, HighestRiskCalculationType, LocationSearchCacheKey, LocationSearchOrder, RiskAssessmentStatus, RiskCalculationItem, RiskCalculationSearchOrder, ViewCalculationMode } from '../../../contracts';
import { CalculationService } from '../../../services';
import { AppConfig } from '../../../app.config';
import { debounce, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { of, Subject, timer } from 'rxjs';
import { AuthService } from '../../../shared';

class StatusFilter {
    public status: RiskAssessmentStatus | undefined;
    public text: string | undefined;
}

@Component({
    selector: 'app-search-calculation',
    templateUrl: './search-calculation.component.html',
    styleUrls: ['./search-calculation.component.css']
})
export class SearchCalculationComponent implements OnInit {
    public isExternalUser: boolean = false;

    public displaySearchResults = false;
    public isSearching = false;
    public initialSearch = true;
    public isContinuing = false;
    public hasMoreSearchResults = false;
    public loading = true;
    public searchOffset = 0;
    public minFactor = 0;
    public maxFactor = null;
    public searchText = '';
    public caseWorkerName = '';

    public caseWorkers: Array<CaseWorker> = [];
    public filteredCaseWorkers: Array<CaseWorker> = [];
    public selectedCaseWorker: CaseWorker | undefined;

    public caseWorkerFilter: CaseWorker | undefined;
    public userFilterScreening = false;
    public userFilterText: string | undefined;

    public orderBy = RiskCalculationSearchOrder.LocationNumber;
    public orderDescending = true;

    public riskAssessmentStatusEnum = RiskAssessmentStatus; // Make enum accessible from the html-template
    public highestRiskCalculationTypeEnum = HighestRiskCalculationType;
    public calculationModelNamesEnum = CalculationModelNames;

    public allSearchResults: Array<RiskCalculationItem> = [];

    public showRiskCalculationMode = ViewCalculationMode.ShowAllCalculations;

    public readonly ViewCalculationMode = ViewCalculationMode; // Make enum accessible from the html-template 
    public readonly riskCalculationSearchOrder = RiskCalculationSearchOrder;
    
    public statusFilters: StatusFilter[] = [
        {
            status: RiskAssessmentStatus.NoRisk,
            text: 'Ingen risiko'
        },
        {
            status: RiskAssessmentStatus.Risk,
            text: 'Risiko'
        }
    ];
    public statusFilter: StatusFilter | undefined;
    
    protected destroyed$ = new Subject<void>();
    private searchSubject = new Subject<string>();
    private destroySearchDebounce$ = new Subject<void>();

    public statusTextMap: Record<CalculationStatus, string> = {
        [CalculationStatus.None]: 'Ikke startet risikoberegning',
        [CalculationStatus.Calculating]: 'Risikoberegning under beregning',
        [CalculationStatus.AwaitingApproval]: 'Afventer godkendelse af risikoberegning',
        [CalculationStatus.Approved]: 'Godkendte risikoberegninger',
        [CalculationStatus.Deleted]: 'Slettet risikoberegning'
    };

    constructor(
        @Inject(LOCALE_ID) private readonly locale: string,
        private readonly authService: AuthService,
        private readonly calculationService: CalculationService,
        private readonly cdref: ChangeDetectorRef
    ) { }

    ngOnInit(): void {

        this.isExternalUser = this.authService.isExternalUser();
        if (this.authService.isOperator()) {
            this.isExternalUser = false;
        }

        this.searchSubject.pipe(
            debounce(() =>
                timer(2000).pipe(
                    takeUntil(this.destroySearchDebounce$)
                )
            ),
            distinctUntilChanged(),
            filter(() => !this.isSearching),
            takeUntil(this.destroyed$))
            .subscribe(() => {
                this.onSearch();
            });
        
        this.GetCaseWorkers();
    }

    public ngAfterViewInit(): void {
        this.onSearch();
        this.cdref.detectChanges();
    }

    public onKeydown(): void {
        this.onSearch();
    }

    public onSearch(): void {
        this.searchOffset = 0;
        this.orderBy = RiskCalculationSearchOrder.LocationNumber;
        this.orderDescending = true;
        this.refreshSearch();
    }

    public onClickSearchButton(): void {
        this.destroySearchDebounce$.next();
        this.onSearch();
    }

    private GetCaseWorkers(): void {
        this.calculationService.getCaseWorkers(this.authService.tryGetUserRegionCode())
        .pipe(
            switchMap((res: any) => {
                const {caseWorkers} = res;
                if (res && caseWorkers && caseWorkers.length > 0) {
                    return of(caseWorkers);
                }
                return of([]);
            })
        )
        .subscribe((result: CaseWorker[]) => {
            this.caseWorkers = result;
            this.filteredCaseWorkers = result;
        });
    }

    private refreshSearch(continueSearch: boolean = false): void {
        if (this.isSearching) {
            return;
        }
        this.isSearching = true;
        this.isContinuing = continueSearch;
        
        this.hasMoreSearchResults = false;

        if (continueSearch) {
            this.searchOffset += AppConfig.settings.maxItems;
        }

        const criteria = {
            searchText: this.searchText,
            riskAssessmentStatus: this.statusFilter,
            caseWorkerSelected: this.selectedCaseWorker,
            minFactor: this.minFactor,
            maxFactor: this.maxFactor,
            orderBy: this.orderBy,
            orderDescending: this.orderDescending,
            skip: this.searchOffset,
            take: AppConfig.settings.maxItems,
            isMyCalculationOnly: this.showRiskCalculationMode === ViewCalculationMode.ShowMineCalculations
        };

        this.calculationService.searchCalculationService(criteria)
            .pipe(
                switchMap((res: any) => {
                    // this.hasMoreSearchResults = result.hasMoreResults;
                    // return [result.items];

                    const isEmpty = (!res.items || res.items.length === 0) && res.total === 0;

                    if (!isEmpty) {
                        // have results, return these locations
                        return of(res);
                    }
                    return of({ items: [], total: 0, continuationToken: null});
                })
            )
            .subscribe((result: any) => {
                this.processSearchResults(result, continueSearch);
            });
    }

    private processSearchResults(result: any, continueSearch: boolean): void {
        if (continueSearch) {
            this.allSearchResults = this.allSearchResults.concat(result.items);
        }
        else {
            this.allSearchResults = result.items;
        }

        this.isSearching = false;
        this.isContinuing = false;
        this.initialSearch = false;
        this.displaySearchResults = this.allSearchResults.length > 0;
        this.hasMoreSearchResults = this.searchOffset + AppConfig.settings.maxItems < result.total;

        result.items = this.allSearchResults;
        this.calculationService.setRiskCalculationsToCache(LocationSearchCacheKey.RiskCalculation, result);
    }

    public onInputChanged(): void {
        this.searchSubject.next(this.searchText);
    }

    public isScreening(location: any): boolean {
        return location.calculationType === HighestRiskCalculationType.Screening;
    }

    public switchMode(mode: ViewCalculationMode): void {
        // change mode and reset result
        this.showRiskCalculationMode = mode;
        this.allSearchResults = [];

        // reset filters
        this.searchOffset = 0;
        this.searchText = '';
        this.statusFilter = undefined;
        this.filterCaseWorker('');
        this.minFactor = 0;
        this.maxFactor = null;
        this.orderBy = RiskCalculationSearchOrder.LocationNumber;
        this.orderDescending = true;

        // call search with new mode and reset filters
        this.refreshSearch();
    }

    public filterCaseWorker(caseWorkerName: string): void {
        if (!caseWorkerName || caseWorkerName.trim().length === 0) {
            this.filteredCaseWorkers = this.caseWorkers;
            this.selectedCaseWorker = undefined;
            this.caseWorkerName = '';
        } else {
            this.filteredCaseWorkers = this.caseWorkers.filter(c => c.name.toLowerCase().indexOf(caseWorkerName.toLowerCase()) >= 0);
            this.selectedCaseWorker = this.filteredCaseWorkers.length === 1 ? this.filteredCaseWorkers[0] : undefined;
        }
    }
    
    public getValidNumber(val: number): number {
        const result = (!val || isNaN(val) || !Number.isFinite(val)) ? 0 : val;
        return result;
    }

    public setOrderBy(orderBy: RiskCalculationSearchOrder): void {
        if (this.orderBy === orderBy) {
            this.orderDescending = !this.orderDescending;
        } else {
            this.orderBy = orderBy;
            this.orderDescending = false;
        }

        this.orderBy = orderBy;
        this.searchOffset = 0;

        this.refreshSearch();
    }

    public getMoreResults(): void {
        this.refreshSearch(true);
    }
}